import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Typeahead, type TypeaheadOption } from '@/components/ui/typeahead';

/**
 * THE PURCHASED BY FILTER — DSH-06 §5a row 4, the dropdown BR-DSH-06-11 is
 * about: "The Purchased by filter must limit every figure on the page."
 *
 * The rule was ignore-marked in four places with the same honest reason: the
 * server and the query plumbing accepted `purchasedById` end to end, and the
 * screen DREW NO CONTROL, so the rule was "not satisfiable from a user's
 * side and citing it would record it as met". This file is the missing
 * control; the limiting itself was already the server's (`created_by`
 * predicates in `reports.service.ts`, both in the report universe and the
 * price-trend query) and the page's (both query keys carry the id).
 *
 * "Who raised the order" (§5a) is the bill's `created_by` — the same
 * identity the detail table's Purchased by column and the server predicate
 * already use, so the filter, the column and the figures cannot disagree
 * about what a purchaser is. §5a's note is why the sales page has no twin:
 * "Sales are credited to a salesperson who earns from them; purchases are
 * traced to a requester who must justify them."
 *
 * ── WHERE THE OPTIONS COME FROM, AND WHY NOT THE FILTERED RESULT ──────────
 *
 * `options` is the report's `purchasers` list: everybody who ever raised a
 * POSTED bill, computed server-side OUTSIDE the window and the other
 * filters. Deriving it from the filtered rows would make the picker narrow
 * itself — choose a warehouse and the list forgets everyone who never
 * bought into it, and the reader can never widen back out through a list
 * that no longer offers them. (The drift card's item picker had exactly
 * this shape of defect, the other way round: a filtered list over
 * unfiltered data. Neither half may borrow the other's population.)
 *
 * `<Typeahead>` rather than `<select>` for GEN-50's reason, verbatim from
 * `warehouse-filter.tsx`: a list can outgrow ten rows, and the search
 * happens here safely because the whole candidate list is already in the
 * browser — nothing is paged, so narrowing hides no matches.
 *
 * BR-DSH-10-09 rides the `clearLabel`: All is a ROW ("All", the delivered
 * default is the page's `useState<string | null>(null)`), so one arrow-key
 * walk covers every choice including taking the narrowing off.
 */

export function PurchasedByFilter({
  options,
  value,
  onChange,
}: {
  /** The report's own roster — `data.purchasers`, `[]` while loading. */
  options: readonly { id: string; name: string }[];
  /** The chosen purchaser's user id, or null for "All". */
  value: string | null;
  onChange: (purchasedById: string | null) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');

  const offered = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    return options
      .filter((person) => needle === '' || person.name.toLowerCase().includes(needle))
      .map((person): TypeaheadOption => ({ id: person.id, label: person.name }));
  }, [options, query]);

  const selected = React.useMemo(() => {
    const held = options.find((person) => person.id === value);
    return held === undefined ? null : { id: held.id, label: held.name };
  }, [options, value]);

  const label = t('reports.colPurchasedBy');

  return (
    <div className="flex flex-col gap-1">
      {/* Band 2's shape: "a grid of dropdowns, each with a small label above it". */}
      <label className="text-xs text-muted-foreground" htmlFor="report-purchased-by">
        {label}
      </label>
      <Typeahead
        id="report-purchased-by"
        label={label}
        value={value}
        onChange={onChange}
        options={offered}
        selected={selected}
        query={query}
        onQueryChange={setQuery}
        size="cell"
        clearLabel={t('reports.all')}
        emptyLabel={t('picker.noMatchingUser')}
      />
    </div>
  );
}
